<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :disabled="isLoading"
    loading-message="Charging for Gourmet..."
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Gourmet Charge
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Caller ID* </label>
        <div class="col-sm-8">
          <sanitized-input
            :id="`callerId-${pageId}`"
            v-model="chargeInfo.callerId"
            placeholder="a8627afb-400c-4d81-8d99-f2c8fe2b885e"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Category* </label>
        <div class="col-sm-5">
          <dropdown-list
            v-model="chargeInfo.category"
            value-key="id"
            :options="categoryList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Currency* </label>
        <div class="col-sm-5">
          <dropdown-list
            v-model="chargeInfo.currency"
            required
            :options="currencyTypes"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Amount* </label>
        <div class="col-sm-5">
          <el-input
            :id="`amount-${pageId}`"
            v-model="chargeInfo.amount"
            min="1"
            type="number"
            step="0.01"
            required
          >
            <template #prepend>
              $
            </template>
          </el-input>
        </div>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label pt-5"> Notes* </label>
        <div class="col-sm-8">
          <sanitized-textarea
            :id="`notes-${pageId}`"
            v-model="chargeInfo.notes"
            placeholder="Please add a note for the charge"
            autosize
            required
          />
        </div>
      </div>
    </template>
  </card-container>
</template>

<script lang="ts">
import { ref, Ref, watch } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { currencyTypes } from '@/constants/app';
import { ChargeType } from '@/models/transaction';
import { ChargeUserForGourmetWithPartnerSubsidyIfAvailableInput } from '@/models/gourmet';
import { chargeUserForGourmetWithPartnerSubsidyIfAvailable } from '@/services/gourmet/chargeUserForGourmetWithPartnerSubsidyIfAvailable';

const pageId = 'gourmet-charge';
const categoryList = [
  { id: ChargeType.Food, key: 'food', label: ChargeType[ChargeType.Food] },
  { id: ChargeType.FoodExtraCharge, key: 'foodExtraCharge', label: ChargeType[ChargeType.FoodExtraCharge] },
];

export default {
  name: 'GourmetCharge',

  setup: () => {
    const { customerInfo } = useCustomer();
    const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

    const chargeInfo: Ref<ChargeUserForGourmetWithPartnerSubsidyIfAvailableInput> = ref({
      callerId: '',
      amount: 0,
      category: categoryList[0].id,
      currency: currencyTypes[0].key,
      notes: '',
    });
    const isLoading = ref(false);

    const submitForm = async () => {
      isLoading.value = true;
      try {
        await chargeUserForGourmetWithPartnerSubsidyIfAvailable({
          callerId: chargeInfo.value.callerId,
          category: chargeInfo.value.category,
          amount: Number(chargeInfo.value.amount),
          currency: chargeInfo.value.currency,
          notes: chargeInfo.value.notes,
        });

        showSuccessPopupDialog({
          text: `$${chargeInfo.value.amount} has been successfully charged for the gourmet item.`,
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      isLoading.value = false;
    };

    const resetForm = () => {
      chargeInfo.value = {
        callerId: customerInfo.value?.id || '',
        amount: 0,
        category: categoryList[0].id,
        currency: currencyTypes[0].key,
        notes: '',
      };
    };

    watch(customerInfo, resetForm, {
      immediate: true,
    });

    return {
      pageId,
      currencyTypes,
      categoryList,
      chargeInfo,
      isLoading,

      util,

      submitForm,
      resetForm,
    };
  },
};
</script>
